<template>
  <div class="fenxiao-withdraw-list">
    <div class="page-header">
      <h2>提现明细</h2>
    </div>

    <!-- 状态导航栏 -->
    <div class="status-tabs">
      <div 
        v-for="item in statusTabs" 
        :key="item.value"
        class="tab-item"
        :class="{ 'active': currentStatus === item.value }"
        @click="switchStatus(item.value)"
      >
        {{ item.label }}
      </div>
    </div>

    <div class="withdraw-list-container" v-loading="loading" element-loading-text="加载中...">

      <div class="list-section">
        <div v-if="withdrawList.length === 0 && !loading" class="empty-state">
          <!-- <img src="/upload/default/default_img/empty.png" alt="暂无数据" /> -->
          <p>暂无提现记录</p>
        </div>

        <div v-else>
          <div v-for="item in withdrawList" :key="item.id" class="withdraw-item">
            <div class="item-header">
              <span class="amount">¥{{ item.money }}</span>
              <span class="status" :class="getStatusClass(item.status)">
                {{ getStatusText(item.status) }}
              </span>
            </div>
            <div class="item-content">
              <p class="time">申请时间：{{ formatTime(item.create_time) }}</p>
              <p v-if="item.audit_time" class="audit-time">
                审核时间：{{ formatTime(item.audit_time) }}
              </p>
              <p v-if="item.remark" class="remark">备注：{{ item.remark }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasMore" class="load-more">
        <el-button @click="loadMore" :loading="loadingMore">加载更多</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { withdrawList } from '@/api/fenxiao/fenxiao'

export default {
  name: 'FenxiaoWithdrawList',
  data() {
    return {
      // 状态导航栏配置
      statusTabs: [
        { label: '全部', value: 0 },
        { label: '待审核', value: 1 },
        { label: '已通过', value: 2 },
        { label: '已拒绝', value: -1 }
      ],
      // 当前选中状态
      currentStatus: 0,
      // 提现记录列表
      withdrawList: [],
      // 分页相关
      currentPage: 1,
      pageSize: 10,
      total: 0,
      hasMore: false,
      // 状态控制
      loading: false,
      loadingMore: false
    }
  },
  mounted() {
    this.getWithdrawList()
  },
  methods: {
    // 获取提现记录列表
    async getWithdrawList(isLoadMore = false) {
      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.currentPage = 1
        this.withdrawList = []
      }
      
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize
        }
        
        // 添加状态筛选 - 根据APP端逻辑调整
        if (this.currentStatus !== 0) {
          params.status = this.currentStatus
        }
        
        const response = await withdrawList(params)
        
        if (response.code === 0 && response.data) {
          const newList = response.data.list || []
          
          if (isLoadMore) {
            this.withdrawList = [...this.withdrawList, ...newList]
          } else {
            this.withdrawList = newList
          }
          
          this.total = response.data.count || 0
          this.hasMore = newList.length === this.pageSize && this.withdrawList.length < this.total
        } else {
          this.$message.error(response.message || '获取提现记录失败')
          if (!isLoadMore) {
            this.withdrawList = []
            this.total = 0
          }
        }
      } catch (error) {
        console.error('获取提现记录失败:', error)
        this.$message.error('获取提现记录失败')
        if (!isLoadMore) {
          this.withdrawList = []
          this.total = 0
        }
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    // 状态切换
    switchStatus(status) {
      this.currentStatus = status
      this.getWithdrawList()
    },

    // 加载更多
    loadMore() {
      if (this.hasMore && !this.loadingMore) {
        this.currentPage++
        this.getWithdrawList(true)
      }
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        '1': '待审核',
        '2': '已通过',
        '-1': '已拒绝'
      }
      return statusMap[status] || '未知'
    },

    // 获取状态样式类
    getStatusClass(status) {
      const classMap = {
        '1': 'status-pending',
        '2': 'status-approved',
        '-1': 'status-rejected'
      }
      return classMap[status] || ''
    },

    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.fenxiao-withdraw-list {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 60px);

  /* 状态导航栏样式 */
  .status-tabs {
    display: flex;
    background: #fff;
    border-radius: 8px;
    padding: 4px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .tab-item {
      flex: 1;
      text-align: center;
      cursor: pointer;
      border-radius: 6px;
      font-size: 14px;
      padding: 10px 0;
      font-weight: 500;
      color: #333;
      transition: all 0.3s ease;
      position: relative;

      &:hover {
        color: #ff547b;
        background: rgba(255, 84, 123, 0.1);
      }

      &.active {
        color: #fff;
        background: #ff547b;
        box-shadow: 0 2px 8px rgba(255, 84, 123, 0.3);
      }
    }
  }

  .page-header {
    margin-bottom: 20px;
    
    h2 {
      color: #333;
      font-size: 24px;
      font-weight: 500;
      margin: 0;
    }
  }

  .withdraw-list-container {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .filter-section {
      margin-bottom: 20px;
      
      .el-select {
        width: 200px;
      }
    }

    .list-section {
      .empty-state {
        text-align: center;
        padding: 60px 0;
        color: #999;
        
        img {
          width: 120px;
          height: 120px;
          margin-bottom: 20px;
        }
        
        p {
          font-size: 16px;
          margin: 0;
        }
      }

      .withdraw-item {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 15px;
        transition: all 0.3s ease;

        &:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;

          .amount {
            font-size: 20px;
            font-weight: 600;
            color: #333;
          }

          .status {
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;

            &.status-pending {
              background-color: #fff7e6;
              color: #fa8c16;
            }

            &.status-approved {
              background-color: #f6ffed;
              color: #52c41a;
            }

            &.status-rejected {
              background-color: #fff2f0;
              color: #ff4d4f;
            }
          }
        }

        .item-content {
          p {
            margin: 8px 0;
            font-size: 14px;
            color: #666;

            &.time {
              color: #999;
            }

            &.audit-time {
              color: #999;
            }

            &.remark {
              color: #333;
            }
          }
        }
      }
    }

    .load-more {
      text-align: center;
      margin-top: 30px;
      
      .el-button {
        padding: 12px 40px;
        font-size: 14px;
      }
    }
  }
}
</style>